/**
 * Frontend Logger Utility
 * 
 * Environment-aware logging that:
 * - Logs everything in development
 * - Only logs errors in production
 * - Sends all errors to centralized logging system at crm.maine-stream.com
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   
 *   logger.log('Debug info');        // Dev only
 *   logger.info('Information');      // Dev only
 *   logger.warn('Warning');          // Dev only
 *   logger.error('Error occurred');  // Always + sent to central logging in all environments
 */

import axios from 'axios';

// Determine environment
const isDevelopment = process.env.NODE_ENV === 'development';

// Centralized error logging configuration
const ERROR_LOGGING_URL = process.env.REACT_APP_ERROR_LOGGING_URL || 'https://crm.maine-stream.com/api';
const ERROR_LOG_ENDPOINT = `${ERROR_LOGGING_URL}/api/error-logs`;
const APPLICATION_NAME = 'utilities-crm-client';

// Log levels
export enum LogLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Error categories
export enum ErrorCategory {
  CLIENT = 'client',
  NETWORK = 'network',
  AUTH = 'auth',
  RENDER = 'render',
  API = 'api'
}

interface ErrorLogOptions {
  stack?: string;
  metadata?: Record<string, any>;
  userId?: string | null;
  statusCode?: number;
  category?: ErrorCategory;
  path?: string;
  method?: string;
}

/**
 * Send error to centralized logging system
 */
const sendErrorToServer = async (
  level: LogLevel,
  message: string,
  options: ErrorLogOptions = {}
): Promise<void> => {
  // Only send errors and critical logs to server (in all environments)
  if (level !== LogLevel.ERROR && level !== LogLevel.CRITICAL) {
    return;
  }

  try {
    const {
      stack,
      metadata = {},
      userId = null,
      statusCode,
      category = ErrorCategory.CLIENT,
      path = window.location.pathname,
      method
    } = options;

    await axios.post(ERROR_LOG_ENDPOINT, {
      application: APPLICATION_NAME,
      environment: process.env.NODE_ENV || 'production',
      timestamp: new Date().toISOString(),
      level,
      message,
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      },
      stack,
      source: 'frontend',
      userId,
      statusCode,
      category,
      path,
      method
    }, {
      timeout: 5000, // 5 second timeout
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    // Silently fail - don't want logging errors to break the app
    if (isDevelopment) {
      console.error('Failed to send error to logging server:', error);
    }
  }
};

/**
 * Determine error category from error object
 */
const determineErrorCategory = (error: any, statusCode?: number): ErrorCategory => {
  // Auth errors
  if (
    statusCode === 401 || 
    statusCode === 403 ||
    error?.message?.toLowerCase().includes('unauthorized') ||
    error?.message?.toLowerCase().includes('forbidden') ||
    error?.message?.toLowerCase().includes('token')
  ) {
    return ErrorCategory.AUTH;
  }

  // Network errors
  if (
    error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('fetch') ||
    error?.code === 'ERR_NETWORK' ||
    statusCode === 0
  ) {
    return ErrorCategory.NETWORK;
  }

  // API errors
  if (statusCode && statusCode >= 400) {
    return ErrorCategory.API;
  }

  // Render errors
  if (
    error?.message?.toLowerCase().includes('render') ||
    error?.message?.toLowerCase().includes('component')
  ) {
    return ErrorCategory.RENDER;
  }

  return ErrorCategory.CLIENT;
};

/**
 * Main logger object
 */
export const logger = {
  /**
   * General logging - only in development
   */
  log: (...args: any[]): void => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Info logging - only in development
   */
  info: (...args: any[]): void => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Warning logging - only in development
   */
  warn: (...args: any[]): void => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Debug logging - only in development
   */
  debug: (...args: any[]): void => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },

  /**
   * Error logging - always enabled + sent to centralized logging
   */
  error: (messageOrError: string | Error, metadata?: Record<string, any>): void => {
    // Always log to console
    console.error(messageOrError, metadata);

    // Send to centralized logging
    const message = messageOrError instanceof Error ? messageOrError.message : messageOrError;
    const stack = messageOrError instanceof Error ? messageOrError.stack : undefined;
    const category = determineErrorCategory(messageOrError, metadata?.statusCode);

    sendErrorToServer(LogLevel.ERROR, message, {
      stack,
      metadata: {
        ...metadata,
        errorType: messageOrError instanceof Error ? messageOrError.name : 'Error'
      },
      category,
      statusCode: metadata?.statusCode,
      userId: metadata?.userId,
      path: metadata?.path,
      method: metadata?.method
    });
  },

  /**
   * Critical error logging - always enabled + sent to centralized logging
   */
  critical: (messageOrError: string | Error, metadata?: Record<string, any>): void => {
    // Always log to console
    console.error('[CRITICAL]', messageOrError, metadata);

    // Send to centralized logging
    const message = messageOrError instanceof Error ? messageOrError.message : messageOrError;
    const stack = messageOrError instanceof Error ? messageOrError.stack : undefined;
    const category = determineErrorCategory(messageOrError, metadata?.statusCode);

    sendErrorToServer(LogLevel.CRITICAL, message, {
      stack,
      metadata: {
        ...metadata,
        errorType: messageOrError instanceof Error ? messageOrError.name : 'CriticalError'
      },
      category,
      statusCode: metadata?.statusCode,
      userId: metadata?.userId,
      path: metadata?.path,
      method: metadata?.method
    });
  },

  /**
   * Table logging - only in development
   */
  table: (data: any): void => {
    if (isDevelopment) {
      console.table(data);
    }
  },

  /**
   * Group logging - only in development
   */
  group: (label: string): void => {
    if (isDevelopment) {
      console.group(label);
    }
  },

  /**
   * Group end - only in development
   */
  groupEnd: (): void => {
    if (isDevelopment) {
      console.groupEnd();
    }
  },

  /**
   * Time logging - only in development
   */
  time: (label: string): void => {
    if (isDevelopment) {
      console.time(label);
    }
  },

  /**
   * Time end - only in development
   */
  timeEnd: (label: string): void => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  }
};

/**
 * Global error handler for uncaught errors
 */
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error(event.error || event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      category: ErrorCategory.CLIENT
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error(event.reason || 'Unhandled Promise Rejection', {
      promise: event.promise,
      category: ErrorCategory.CLIENT
    });
  });
}

export default logger;
